<script setup lang="ts">
// src/SideWinder.Web/src/components/strategy/AllocationHero.vue
//
// Hero band at the top of the Strategy tab. v2 (2026-09-05, paolo spec):
// plan-vs-actual, not plan-only. One bar = 100% of capital; each enabled
// fund keeps its TARGET width, and within the segment the actually-held
// fraction is coloured solid while the undeployed remainder stays grey —
// the grey across the bar IS the cash. OTP CASH is a chip (not a bar
// segment — that would double-count the grey) showing the MEASURED
// post-09:00 residual from GET /api/user/allocation-status, £ and % of
// capital, dated when it isn't today's number. When the grey area exceeds
// the measured residual beyond tolerance, the gap is sale proceeds inside
// the settlement cycle (sell by 09:00 today → investable before 09:00
// tomorrow) and a muted "settling" note says so.
import { ref, computed, onMounted } from 'vue'
import { api, type AllocationStatus } from '../../api/client'
import { FUND_META, CASH_COLOR } from '../fundPalette'

const status = ref<AllocationStatus | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)

// Fund colours: the shared palette (components/fundPalette.ts).

interface Slice {
  key: string
  label: string
  color: string
  targetPct: number
  widthPct: number      // legacy: the target weight; shared pot: the held share of the pot
  fillPct: number       // 0..100, share of the segment that is coloured
  heldPct: number       // held value as a % of capital
  positionGbp: number
  pendingBuyGbp: number // part of positionGbp bought today, contract note not yet booked
}

const capital = computed(() => status.value?.capitalGbp ?? 0)

// Spec 2026-10-02 §6.4: for a shared-pot user the weights are per-fund ENTRY TARGETS (% of pot
// value) that can sum above 100, so a "one bar = 100 % of capital, segment = target" picture is
// wrong. Shared pot: each segment is what is HELD; the grey remainder is cash.
const isSharedPot = computed(() => status.value?.allocationModel === 'shared_pot')

const slices = computed<Slice[]>(() => {
  const s = status.value
  if (!s) return []
  const out: Slice[] = []
  for (const f of s.funds) {
    const meta = FUND_META[f.fund]
    if (!meta) continue
    const heldPctOfCapital = capital.value > 0 ? (f.positionGbp / capital.value) * 100 : 0
    if (isSharedPot.value) {
      if (!f.enabled && f.positionGbp <= 0.5) continue
      out.push({
        key: f.fund, label: meta.label, color: meta.color,
        targetPct: f.weightPct,
        widthPct: Number(heldPctOfCapital.toFixed(2)),
        fillPct: 100,
        heldPct: heldPctOfCapital,
        positionGbp: f.positionGbp,
        pendingBuyGbp: f.pendingBuyGbp ?? 0,
      })
      continue
    }
    if (!f.enabled) continue
    const rawFill = f.weightPct > 0
      ? Math.min(1, heldPctOfCapital / f.weightPct)
      : (f.positionGbp > 0.5 ? 1 : 0)
    out.push({
      key: f.fund,
      label: meta.label,
      color: meta.color,
      targetPct: f.weightPct,
      widthPct: f.weightPct,
      fillPct: Number((rawFill * 100).toFixed(2)),
      heldPct: heldPctOfCapital,
      positionGbp: f.positionGbp,
      pendingBuyGbp: f.pendingBuyGbp ?? 0,
    })
  }
  return out
})

const unallocatedPct = computed(() => {
  const sum = slices.value.reduce((a, s) => a + s.widthPct, 0)
  const rest = Number((100 - sum).toFixed(2))
  return rest >= 0.05 ? rest : 0
})

const residual = computed(() => status.value?.residual ?? { available: false, totalGbp: 0, date: null })
const residualPct = computed(() =>
  residual.value.available && capital.value > 0
    ? (residual.value.totalGbp / capital.value) * 100
    : 0)

// The OTP row's own track (paolo 2026-09-05): same 100%-of-capital scale
// as the main bar so the sleeve's size reads directly against the fund
// segments. Solid purple = measured residual; lighter purple = account
// cash that is NOT yet OTP-spendable (sale proceeds inside the settlement
// cycle: sell by 09:00 → investable before 09:00 tomorrow).
const cashPct = computed(() =>
  capital.value > 0 && status.value ? (status.value.cashGbp / capital.value) * 100 : 0)
const residualFillPct = computed(() =>
  Number(Math.min(100, residualPct.value).toFixed(2)))
const settlingPct = computed(() => {
  const raw = residual.value.available
    ? Math.max(0, cashPct.value - residualPct.value)
    : cashPct.value // no measurement — the whole cash share is unconfirmed
  return Number(raw.toFixed(2))
})

const SETTLING_TOLERANCE_PP = 1.5
const settlingGbp = computed(() => {
  if (!residual.value.available || capital.value <= 0) return 0
  return settlingPct.value > SETTLING_TOLERANCE_PP
    ? Math.round((settlingPct.value / 100) * capital.value)
    : 0
})

const todayIso = () => {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

const residualAsOf = computed<string | null>(() => {
  const date = residual.value.date
  if (!residual.value.available || !date || date === todayIso()) return null
  try {
    return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB',
      { day: 'numeric', month: 'short', timeZone: 'UTC' })
  } catch {
    return date
  }
})

function fmtPct(pct: number): string {
  return `${Number(pct.toFixed(2))}%`
}
function fmtGbp(x: number): string {
  return `£${Math.round(x).toLocaleString('en-GB')}`
}
function fillSuffix(s: Slice): string | null {
  if (s.positionGbp <= 0.5) return 'no position'
  // ≥97.5% is effectively full — display it as 100% rather than nagging
  // "99% filled" for a fund sitting within drift of target (paolo 09-05).
  if (s.fillPct >= 97.5) return '100% filled'
  return `${Math.round(s.fillPct)}% filled`
}

onMounted(async () => {
  // Allocation is static (docs/analysis/2026-09-14-static-weight-t1-sweep.md);
  // the monthly rotation-tilt "next rebalance" fetch was removed 2026-09-15.
  try {
    status.value = await api.allocationStatus()
  } catch (e) {
    loadError.value = (e as any)?.message ?? 'Failed to load allocation status'
  }
  loading.value = false
})
</script>

<template>
  <div class="hero">
    <div v-if="loading" class="loading-text">Loading allocation…</div>
    <div v-else-if="loadError" class="msg-error">{{ loadError }}</div>
    <template v-else>
      <div class="hero-top">
        <span class="hero-title">{{ isSharedPot ? 'Current Allocation — shared pot' : 'Current Allocation' }}</span>
      </div>
      <div class="alloc-bar" role="img" aria-label="Current allocation split">
        <div
          v-for="s in slices"
          :key="s.key"
          class="alloc-seg"
          :data-testid="`alloc-seg-${s.key}`"
          :style="{ width: `${s.widthPct}%` }"
          :title="`${s.label} target ${fmtPct(s.targetPct)} — held ${fmtGbp(s.positionGbp)}`"
        >
          <div
            class="alloc-fill"
            :data-testid="`alloc-fill-${s.key}`"
            :style="{ width: `${s.fillPct}%`, background: s.color }"
          />
        </div>
        <div
          v-if="unallocatedPct > 0"
          class="alloc-seg"
          data-testid="alloc-seg-UNALLOC"
          :style="{ width: `${unallocatedPct}%` }"
          :title="isSharedPot ? 'Cash' : 'Unallocated'"
        />
      </div>
      <div class="alloc-chips">
        <span v-for="s in slices" :key="s.key" class="chip" :data-testid="`alloc-chip-${s.key}`">
          <span
            class="chip-dot"
            :class="{ hollow: s.positionGbp <= 0.5 }"
            :style="s.positionGbp > 0.5 ? { background: s.color } : { borderColor: s.color }"
          />
          <template v-if="isSharedPot">
            {{ s.label }} {{ s.positionGbp > 0.5 ? `${fmtPct(Number(s.heldPct.toFixed(1)))} held` : 'no position' }}
            <span v-if="s.pendingBuyGbp > 0.5" class="chip-note" :data-testid="`alloc-pending-${s.key}`">· bought today, booking pending</span>
            <span class="chip-note">· entry target {{ fmtPct(s.targetPct) }}</span>
          </template>
          <template v-else>
            {{ s.label }} {{ fmtPct(s.targetPct) }}
            <span v-if="fillSuffix(s)" class="chip-note">· {{ fillSuffix(s) }}</span>
          </template>
        </span>
      </div>
      <p v-if="isSharedPot" class="shared-pot-note" data-testid="shared-pot-note">
        Entry targets are per fund, as a % of the pot, and can add up to more than 100%: funds take cash in
        tier order from settled cash. The grey part of the bar is cash.
      </p>
      <div class="otp-row">
        <div class="otp-label">OTP CASH</div>
        <div
          class="otp-track"
          role="img"
          aria-label="OTP cash sleeve as a share of capital"
          title="Same scale as the bar above: % of total capital"
        >
          <div
            class="otp-fill"
            data-testid="otp-fill"
            :style="{ width: `${residualFillPct}%` }"
          />
          <div
            class="otp-settling"
            data-testid="otp-settling"
            :style="{ width: `${settlingPct}%` }"
          />
        </div>
        <div class="otp-legend">
          <span class="chip" data-testid="otp-cash-chip">
            <span
              class="chip-dot"
              :class="{ hollow: !residual.available || residual.totalGbp <= 0.5 }"
              :style="residual.available && residual.totalGbp > 0.5
                ? { background: CASH_COLOR }
                : { borderColor: CASH_COLOR }"
            />
            <template v-if="residual.available">
              OTP CASH {{ fmtGbp(residual.totalGbp) }} = {{ fmtPct(residualPct) }}
              <span v-if="residualAsOf" class="chip-note">· as of {{ residualAsOf }}</span>
            </template>
            <template v-else>
              OTP CASH — <span class="chip-note">· no 09:00 measurement</span>
            </template>
          </span>
          <span v-if="settlingGbp > 0" class="chip-note" data-testid="settling-note">
            ≈ {{ fmtGbp(settlingGbp) }} settling — sale proceeds investable before 09:00 next dealing day
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.hero {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}
.hero-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.hero-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6edf3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alloc-bar {
  display: flex;
  gap: 2px;               /* 2px surface gap between adjacent fills */
  height: 14px;
  border-radius: 4px;
  overflow: hidden;
  background: #0d1117;
}
.alloc-seg {
  min-width: 0;
  background: #2d333b;    /* undeployed target — the grey IS the cash */
}
.alloc-fill { height: 100%; }

.alloc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  margin-top: 0.6rem;
  font-size: 0.85rem;
  color: #c9d1d9;
}
.chip { display: inline-flex; align-items: center; gap: 0.35rem; white-space: nowrap; }
.chip-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.chip-dot.hollow { background: transparent; border: 1.5px solid #6e7681; }
.chip-note { color: #6e7681; }

.otp-row { margin-top: 0.85rem; }
.otp-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #8b949e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.3rem;
}
.otp-track {
  display: flex;
  height: 10px;
  border-radius: 4px;
  overflow: hidden;
  background: #0d1117;
}
.otp-fill { background: #b98af0; height: 100%; }
.otp-settling { background: rgba(185, 138, 240, 0.3); height: 100%; }
.otp-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 1rem;
  margin-top: 0.4rem;
  font-size: 0.85rem;
  color: #c9d1d9;
}

.shared-pot-note { margin: 0.5rem 0 0; color: #8b949e; font-size: 0.78rem; line-height: 1.5; }

.loading-text { color: #6e7681; font-size: 0.9rem; font-style: italic; }
.msg-error {
  padding: 0.4rem 0.75rem;
  background: rgba(248,81,73,0.1);
  border: 1px solid #f85149;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #f85149;
}

@media (max-width: 600px) {
  .hero { padding: 0.75rem 0.9rem; }
  .alloc-chips { gap: 0.3rem 0.75rem; font-size: 0.8rem; }
}
</style>
